import {translations} from '../../i18n/translations';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {useAuth} from '../../contexts/auth/context';
import {useTranslation} from 'react-i18next';
import {UsersRepository} from '../../repositories/users';
import {Product} from '../../backend/enums/product';
import {UserType} from '../../backend/enums/user-type';
import {useSnackBar} from '../../contexts/snack-bar/context';
import DeleteResourceConfirmationModal from '../delete-resource-confirmation-modal';
import {InvitesRepository} from '../../repositories/invites';
import {RelationshipInviteData} from '../../backend/interfaces/relationship-invite-data';
import {Invite} from '../../backend/interfaces/invite';

export interface DeleteUserModalProps {
  deleteDialogOpened: boolean;
  setDeleteDialogOpened: (opened: boolean) => void;
  handleDeleteDialogOpened: (opened: boolean) => void;
  data: AffiliatedUser;
  onDelete?: (data: AffiliatedUser) => void;
}

export const DeleteUserModal = ({
  deleteDialogOpened,
  setDeleteDialogOpened,
  handleDeleteDialogOpened,
  data,
  onDelete,
}: DeleteUserModalProps) => {
  const {t} = useTranslation();
  const authContext = useAuth();
  const snackBarContext = useSnackBar();

  // Dirty Hack
  const isPendingInvite =
    (data.user as unknown as Invite<RelationshipInviteData>).state === 'pending';

  // Handle delete user
  const handleDeleteUser = () => {
    if (!data || !data.user.id) {
      console.error('🔴 Error deleting user');
      return;
    }
    // Change dialog state
    setDeleteDialogOpened(false);

    UsersRepository.instance
      .deleteUserInEntity(authContext.state.session?.selectedAffiliation?.entity?.id!, data.user.id)
      .then(() => {
        onDelete && onDelete(data);
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_form.success.success_delete_caregiver, {
            name: data.user.name,
          }),
        });
      })
      .catch((error) => {
        snackBarContext
          .setOfficeToastNotification({
            severity: 'error',
            message: t(translations.user_form.errors.error_on_delete_caregiver_button),
          })
          .then(() => {
            console.error('🔴 Error deleting user', error);
          });
      });
  };

  // Handle mark senior as inactive
  const handleMarkSeniorAsInactive = () => {
    if (!data) {
      console.error('🔴 Error deleting user');
      return;
    }

    // Change dialog state
    setDeleteDialogOpened(false);

    UsersRepository.instance
      .updateById(data.user.id!, {
        name: data.user.name,
        product: Product.Bluebird,
        type: UserType.Senior,
        isActive: false,
      })
      .then(() => {
        onDelete && onDelete(data);
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_card_form.success.senior_inactive_success, {
            name: data.user.name,
          }),
        });
      })
      .catch((error) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.user_card_form.errors.error_on_marking_senior_as_inactive),
        });
        console.error('🔴 Error marking senior as inactive', error);
      });
  };

  const handleDeleteInvite = () => {
    if (!data || !data.user.id) {
      console.error('🔴 Error deleting invite');
      return;
    }
    // Change dialog state
    setDeleteDialogOpened(false);

    InvitesRepository.instance
      .cancelInvite(data.user.id)
      .then(() => {
        onDelete && onDelete(data);
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_form.success.success_delete_invite, {
            name: data.user.name,
          }),
        });
      })
      .catch((error) => {
        snackBarContext
          .setOfficeToastNotification({
            severity: 'error',
            message: t(translations.user_form.errors.error_on_delete_invite),
          })
          .then(() => {
            console.error('🔴 Error deleting invite', error);
          });
      });
  };

  return (
    <DeleteResourceConfirmationModal
      id="confirm-delete-user-dialog"
      open={deleteDialogOpened}
      title={
        data.user.type === 'caregiver'
          ? t(translations.professional_caregivers.root.title)
          : t(translations.seniors.root.title)
      }
      description={
        data.user.type === 'caregiver'
          ? isPendingInvite
            ? t(translations.user_form.delete_caregiver_invite_title, {
                name: data.user.name,
              })
            : t(translations.user_form.delete_caregiver_dialog_title, {
                name: data.user.displayName || data.user.name,
              })
          : t(translations.user_form.delete_senior_dialog_title, {
              name: data.user.displayName || data.user.name,
            })
      }
      cannotBeUndoneMessage={
        data.user.type === 'caregiver'
          ? isPendingInvite
            ? t(translations.user_form.delete_caregiver_invite_content, {
                name: data.user.name,
              })
            : t(translations.user_form.delete_caregiver_dialog_content, {
                name: data.user.displayName || data.user.name,
                entity: authContext.state.session?.selectedAffiliation?.entity?.name || '',
              })
          : t(translations.user_form.delete_senior_dialog_content)
      }
      onCancel={() => handleDeleteDialogOpened(false)}
      onConfirm={
        data.user.type === 'caregiver'
          ? isPendingInvite
            ? handleDeleteInvite
            : handleDeleteUser
          : handleMarkSeniorAsInactive
      }
      cancel_label={
        isPendingInvite
          ? t(
              translations.senior_relationships_invites_history
                .cancel_invite_dialog_action_no_button
            )
          : undefined
      }
      confirm_label={
        isPendingInvite
          ? t(
              translations.senior_relationships_invites_history
                .cancel_invite_dialog_action_yes_button
            )
          : undefined
      }
    />
  );
};
